// The status vocabulary of the map, as on the dashboard's overview and the back-office's sensors page: worst first.
// Pure functions, without MapLibre, so that pages can sort, count and filter before the map loads.

import type { AssetStatusCode } from '@cck/schema';

export type { AssetStatusCode };

export const STATUS_ORDER: readonly AssetStatusCode[] = ['alarm', 'offline', 'warning', 'ok', 'no_data'];

export type StatusCounts = Record<AssetStatusCode, number>;

export const emptyCounts = (): StatusCounts => ({ alarm: 0, offline: 0, warning: 0, ok: 0, no_data: 0 });

/** The worst of some statuses (alarm > offline > warning > ok > no data); no data for none. */
export function worstStatus(list: Iterable<AssetStatusCode>): AssetStatusCode {
  let worst = STATUS_ORDER.length - 1;
  for (const s of list) worst = Math.min(worst, STATUS_ORDER.indexOf(s));
  return STATUS_ORDER[worst]!;
}

export function countStatuses(list: Iterable<AssetStatusCode>): StatusCounts {
  const c = emptyCounts();
  for (const s of list) c[s] += 1;
  return c;
}

/** Sorts worst first. */
export const byStatus = (a: AssetStatusCode, b: AssetStatusCode) => STATUS_ORDER.indexOf(a) - STATUS_ORDER.indexOf(b);

/** The statuses that need a look: the "problems only" filter. */
export const PROBLEMS: readonly AssetStatusCode[] = ['alarm', 'offline', 'warning'];

/** A latitude and a longitude typed by a person: decimal point or comma, within range; null otherwise. */
export function parseCoordinate(text: string, kind: 'lat' | 'lon'): number | null {
  const t = text.trim().replace(',', '.');
  if (!/^-?\d{1,3}(\.\d+)?$/.test(t)) return null;
  const v = Number(t);
  const max = kind === 'lat' ? 90 : 180;
  return Number.isFinite(v) && Math.abs(v) <= max ? v : null;
}

/** Coordinates rounded to 6 decimals (about 10 cm), as stored. */
export const roundCoordinate = (v: number) => Math.round(v * 1e6) / 1e6;
